import Loader from 'components/Loader';
import { ScrollArea } from 'components/ui/scroll-area';
import { QUESTIONNAIRE_CODE } from 'lib/constants';
import { getParsedAnswers } from 'lib/questionnaire';
import { checkSpanishLocale, extractLabelAndComment } from 'lib/utils';
import { Loader2, Pencil, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { PATH_ROUTES } from 'routes/path';
import {
  getQuestionnaireV3,
  postQuestionnaireOptionSearchV3,
} from 'services/profile/api';
import { useGetQuestionnaireV3 } from 'services/profile/hooks';
import { HealthInfoItem } from 'services/profile/type';
import { useQuestionnaireStore } from 'store/questionnaire';
import CompleteQuestionnaire from './components/CompleteQuestionnaire';
import { Button } from 'components/ui/button';
import useAuthStore from 'store/auth';
import { useTranslation } from 'react-i18next';

function Questionnaire() {
  const { t } = useTranslation();
  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const [viewQuestionnaire, setViewQuestionnaire] = useState(false);

  const { data, isLoading } = useGetQuestionnaireV3({
    payload: { retrieve_type: 'all', profileId },
  });

  const listData = useMemo(() => {
    if (!data || !data?.data || !data?.data.length) {
      return [];
    }

    const parsedItems = getParsedAnswers(data.data);
    const healthData = parsedItems.map((question) => {
      // const answer = question?.[isSpanish ? 'spanish_choices' : 'eng_choices'];
      const answer = question?.eng_choices;
      if (!answer) {
        return {
          ...question,
          answer: '',
        };
      }

      let parsedAnswer: any = '';
      if (Array.isArray(answer)) {
        const allString = answer.every((item) => typeof item === 'string');
        if (allString) {
          parsedAnswer = answer.join(', ');
        } else {
          answer.forEach((item, index) => {
            if (typeof item === 'string') {
              parsedAnswer += `${item}, \n`;
              return;
            }

            if (item?.id || item?.choice) {
              parsedAnswer += `${item?.choice}, \n`;
              return;
            }

            if (item && Object.keys(item).length) {
              const [key, value] = Object.entries(item)[0];
              const parsedItemValue = Array.isArray(value)
                ? value.join(', ')
                : value;
              parsedAnswer += `${key}: ${parsedItemValue}${
                answer.length - 1 === index ? '' : ', \n'
              }`;
            }
          });
        }
      } else {
        parsedAnswer = answer || '';
      }

      return {
        ...question,
        answer: parsedAnswer,
      };
    });
    return healthData;
  }, [data]);

  if (isLoading) {
    return <Loader className='min-h-screen' />;
  }

  const hasAnswered = data?.data?.every((item) => !!item?.answer_id);
  const isAllEmpty = listData.every((item) => item.answer === '');

  const onViewQuestionnaire = () => {
    setViewQuestionnaire(true);
  };

  if (!hasAnswered && !viewQuestionnaire) {
    return (
      <CompleteQuestionnaire
        isAllEmpty={isAllEmpty}
        onView={onViewQuestionnaire}
      />
    );
  }

  return (
    <ScrollArea className='h-full sm:h-[calc(100vh-5.5rem)] lg:h-[calc(100vh-2rem)] ml-4 sm:ml-0 px-4 my-4 mr-4 space-y-4 border rounded-md shadow-sm relative'>
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <h1 className='text-lg font-semibold lg:text-xl'>
            {t('questinnaire')}
          </h1>
          {!hasAnswered ? (
            <Button
              onClick={() => setViewQuestionnaire(false)}
              className='p-0 rounded-full size-5 lg:size-8 bg-tertiary/90 hover:bg-tertiary'
            >
              <X className='text-white size-4' />
            </Button>
          ) : null}
        </div>
        <p className='text-sm lg:text-base font-medium text-[#656D76]'>
          {t('questionnaire_desc')}
        </p>
      </div>

      <div className='pb-8 mt-4 space-y-4'>
        {listData?.length
          ? listData.map((item, index) => (
              <QuestionnaireItem item={item} index={index} />
            ))
          : null}
      </div>
    </ScrollArea>
  );
}

function QuestionnaireItem({
  item,
  index,
}: {
  item: HealthInfoItem & { answer: string };
  index: number;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setQuestionData = useQuestionnaireStore(
    (store) => store.setQuestionData
  );

  const [isLoading, setIsLoading] = useState(false);

  const question = item?.eng_question;
  const { label } = extractLabelAndComment(question ? question.toString() : '');
  const isSpanish = checkSpanishLocale();

  let ansLabel = '',
    ansComment = '';
  if ([QUESTIONNAIRE_CODE.TAKING_MEDICATIONS].includes(item.code)) {
    ansLabel = item?.answer || '';
  } else if (
    [
      QUESTIONNAIRE_CODE.FAMILY_MEDICAL_CONDITIONS,
      QUESTIONNAIRE_CODE.MEDICAL_CONDITIONS,
    ].includes(item.code)
  ) {
    const choices = item.eng_choices;
    if (typeof choices === 'string') {
      const { label: answerLabel, comment } = extractLabelAndComment(
        item?.answer ? item.answer.toString() : ''
      );
      ansLabel = answerLabel;
      ansComment = comment;
    }

    let familyAnswer = '';
    if (Array.isArray(choices)) {
      const isObject = choices.every((ch) => typeof ch === 'object');
      const choicesArr = isObject
        ? choices
        : choices.map((cho) =>
            typeof cho === 'string' ? cho : Object.values(cho)[0]
          );
      choicesArr.forEach((choiceItem, index) => {
        const choice = isObject ? choiceItem.choice : choiceItem;
        if (choice) {
          const { label: answerLabel, comment } =
            extractLabelAndComment(choice);
          familyAnswer =
            familyAnswer +
            (index === 0 ? '' : ', ') +
            answerLabel +
            (comment ? ` - ${comment}` : '');
        }
      });
    }
    ansLabel = familyAnswer;
  } else {
    const { label: answerLabel, comment } = extractLabelAndComment(
      item?.answer ? item.answer.toString() : ''
    );
    ansLabel = answerLabel;
    ansComment = comment;
  }

  const onEdit = async () => {
    try {
      setIsLoading(true);
      const res = (await getQuestionnaireV3({
        retrieve_type: 'single_question',
        question_sequence: item.question_sequence,
      })) as any;
      const questionnaireItem = res?.data?.[0];
      if (!questionnaireItem) {
        return;
      }

      let choice = item.answer;
      if (item.code === QUESTIONNAIRE_CODE.TAKING_MEDICATIONS) {
        if (!item.answer) {
          choice = JSON.stringify(item.answer);
        } else {
          const medicines = item.answer
            ?.split(',')
            .map((med) => med.trim())
            .map((med) => {
              const splitItem = med.split('-');
              let name = '';
              if (splitItem.length === 4) {
                name = `${splitItem[0]}-${splitItem[1]}`;
              }
              if (splitItem.length <= 3) {
                name = splitItem[0];
              }
              return name;
            });

          const medicineWithDoses = (await postQuestionnaireOptionSearchV3({
            combos: medicines,
          })) as any;
          const selectedMedicines = medicineWithDoses?.length
            ? medicineWithDoses.map(
                (
                  med: { name: string; doses: { dose: string; id: string }[] },
                  index: number
                ) => {
                  const splitItem = item.answer.split(',')[index].split('-');

                  let selectedDose = '';
                  if (splitItem.length > 1) {
                    const medDose = med.doses?.length
                      ? med.doses.find((dose) => dose.dose === splitItem.at(-2))
                          ?.id
                      : splitItem.at(-2);
                    selectedDose = medDose ?? '';
                  }
                  return {
                    ...med,
                    selectedDose,
                    quantity: splitItem.length > 1 ? splitItem.at(-1) : 1,
                  };
                }
              )
            : [];
          choice = JSON.stringify(selectedMedicines);
        }
      }

      if (
        [
          QUESTIONNAIRE_CODE.MEDICAL_CONDITIONS,
          QUESTIONNAIRE_CODE.FAMILY_MEDICAL_CONDITIONS,
        ].includes(item.code)
      ) {
        try {
          const itemRes = res?.data?.[0];
          const conditions = itemRes
            ? itemRes?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices']
            : '';
          choice = conditions || item.answer;
        } catch (error) {
          choice = item.answer;
        }
      }

      setQuestionData({ ...questionnaireItem, choice });
      navigate(`${PATH_ROUTES.QUESTIONNAIRE}?code=${questionnaireItem.code}`);
    } catch (error) {
      toast.error(t('failed_to_get_question_details'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='p-4 pt-3 space-y-2 bg-primary/10 rounded-2xl'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <div className='grid bg-white rounded-full size-6 place-content-center'>
            <p className='text-sm font-medium'>{index + 1}</p>
          </div>
          <p className='text-sm font-semibold lg:text-base'>{label}</p>
        </div>
        <button
          className='grid bg-white rounded-full size-5 lg:size-7 place-content-center shrink-0'
          onClick={onEdit}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className='text-gray-400 animate-spin size-3 lg:size-4' />
          ) : (
            <Pencil className='size-3 lg:size-4 text-primary' />
          )}
        </button>
      </div>
      <div className='px-4 py-2 bg-white rounded-xl'>
        <p className='text-sm whitespace-pre-wrap'>
          {ansLabel} {ansComment ? `- ${ansComment}` : ''}
        </p>
      </div>
    </div>
  );
}

export default Questionnaire;
